import React, { useEffect, useMemo, useState } from 'react';

type HTMLImageProps = React.DetailedHTMLProps<
  React.ImgHTMLAttributes<HTMLImageElement>,
  HTMLImageElement
>;
type ImageWithBackupsProps = Omit<HTMLImageProps, 'src' | 'onError'> & {
  imageSources: string[];
  defaultImage: string;
};

export const ImageWithBackups = (props: ImageWithBackupsProps): JSX.Element => {
  const { imageSources, defaultImage, ...imageProps } = props;
  const [imageSourceIndex, setImageSourceIndex] = useState(0);

  useEffect(
    function resetImageSourceIndex() {
      setImageSourceIndex(0);
    },
    [imageSources],
  );

  const imageSource = useMemo(() => {
    // If there are source images to try, use them.
    if (imageSourceIndex < imageSources.length) {
      return imageSources[imageSourceIndex];
    }
    // If we've tried all source images, use the default image.
    if (imageSourceIndex === imageSources.length) {
      return defaultImage;
    }
    // If the default image is also broken, return an empty string.
    return '';
  }, [imageSources, imageSourceIndex, defaultImage]);

  return (
    <img
      src={imageSource}
      onError={(): any => {
        // [0, imageSources.length): Try source images.
        // [imageSources.length]: Use default image.
        // [imageSources.length + 1]: Use the empty string, stop updating the element.
        if (imageSourceIndex < imageSources.length + 1) {
          setImageSourceIndex(imageSourceIndex + 1);
        }
      }}
      {...imageProps}
    />
  );
};
